import { useEffect, useState } from 'react';
import { Globe, X } from 'lucide-react';
import { useI18n, LOCALES, translateFor } from '../i18n';

const FLAG_KEY = 'gd_lang_suggested_v1';

/**
 * One-time, first-visit nudge: if the browser language maps to a supported
 * locale that differs from the current UI language, offer a one-tap switch.
 * The prompt is rendered in the *target* language (via translateFor) so the
 * user can read it. Shown at most once (flag persisted in localStorage).
 */
export default function LanguageWelcome() {
    const { locale, setLocale } = useI18n();
    const [target, setTarget] = useState(null);

    useEffect(() => {
        try {
            if (localStorage.getItem(FLAG_KEY)) return;
            const codes = LOCALES.map((l) => l.code);
            const langs = navigator.languages && navigator.languages.length
                ? navigator.languages
                : [navigator.language || ''];
            let match = null;
            for (const raw of langs) {
                if (!raw) continue;
                const full = raw; // e.g. "pt-BR"
                const base = raw.split('-')[0]; // e.g. "pt"
                match = codes.find((c) => c.toLowerCase() === full.toLowerCase())
                    || codes.find((c) => c.toLowerCase() === base.toLowerCase());
                if (match) break;
            }
            // Treat en-US and en as equivalent so we don't nudge between them.
            const sameBase = match && match.split('-')[0] === (locale || '').split('-')[0];
            if (!match || match === locale || sameBase) {
                localStorage.setItem(FLAG_KEY, '1');
                return;
            }
            setTarget(match);
            localStorage.setItem(FLAG_KEY, '1');
        } catch { /* ignore */ }
    }, [locale]);

    if (!target) return null;

    const label = (LOCALES.find((l) => l.code === target) || {}).label || target;
    const flag = (LOCALES.find((l) => l.code === target) || {}).flag || '🌍';
    const question = translateFor(target, 'location.lang_suggest').replace('{lang}', label);
    const switchLabel = translateFor(target, 'location.lang_switch');
    const keepLabel = translateFor(target, 'location.lang_keep');

    const choose = (code) => { if (code) setLocale(code); setTarget(null); };

    return (
        <div
            data-testid="language-welcome"
            className="fixed inset-x-3 top-banner-safe sm:left-1/2 sm:-translate-x-1/2 sm:inset-x-auto sm:w-[420px] z-[110] animate-fade-up"
        >
            <div className="bg-brand-card border border-brand-border rounded-2xl shadow-lg p-4">
                <div className="flex items-start gap-3">
                    <div className="bg-brand-green/10 text-brand-green w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Globe size={18} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-brand-ink font-medium leading-snug">
                            <span className="mr-1">{flag}</span>{question}
                        </p>
                        <div className="mt-3 flex gap-2">
                            <button
                                data-testid="lang-welcome-switch"
                                onClick={() => choose(target)}
                                className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-2 rounded-full text-xs font-semibold transition-colors"
                            >
                                {switchLabel}
                            </button>
                            <button
                                data-testid="lang-welcome-keep"
                                onClick={() => choose(null)}
                                className="flex-1 bg-brand-bg2 hover:bg-brand-border text-brand-ink py-2 rounded-full text-xs font-medium transition-colors"
                            >
                                {keepLabel}
                            </button>
                        </div>
                    </div>
                    <button
                        data-testid="lang-welcome-close"
                        onClick={() => choose(null)}
                        className="text-brand-ink3 hover:text-brand-ink p-1 -mt-1"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}
